import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {useMutation} from "@khan/data-gql";
import {doNotTranslateYet} from "@khan/i18n";

import {HIDE_PROGRAM_EVERYWHERE} from "#scratchpads-package/queries/hide-program-everywhere.ts";
import {HIDE_PROGRAM_FROM_HOTLIST} from "#scratchpads-package/queries/hide-program-from-hotlist.ts";

import cloudWarning from "@phosphor-icons/core/regular/cloud-warning.svg";

type Props = {
    programId: string;
    hiddenEverywhere: boolean;
    hiddenFromHotlist: boolean;
};

export default function HideButtons(props: Props): React.ReactElement {
    const [hiddenEverywhere, setHiddenEverywhere] = React.useState(
        props.hiddenEverywhere,
    );
    const [hiddenFromHotlist, setHiddenFromHotlist] = React.useState(
        props.hiddenFromHotlist,
    );

    const [hideEverywhere, hideEverywhereResult] = useMutation(
        HIDE_PROGRAM_EVERYWHERE,
    );
    const [hideFromHotlist, hideFromHotlistResult] = useMutation(
        HIDE_PROGRAM_FROM_HOTLIST,
    );

    const handleHideEverywhere = async () => {
        const newHiddenEverywhere = !hiddenEverywhere;
        const result = await hideEverywhere({
            programID: props.programId,
            hide: newHiddenEverywhere,
        });
        if (
            result.status !== "success" ||
            result.data.hideProgramEverywhere?.error != null
        ) {
            return;
        }
        setHiddenEverywhere(newHiddenEverywhere);
        if (newHiddenEverywhere) {
            setHiddenFromHotlist(true);
        }
    };

    const handleHideFromHotlist = async () => {
        const newHiddenFromHotlist = !hiddenFromHotlist;
        const result = await hideFromHotlist({
            programID: props.programId,
            hide: newHiddenFromHotlist,
        });
        if (
            result.status !== "success" ||
            result.data.hideProgramFromHotlist?.error != null
        ) {
            return;
        }
        setHiddenFromHotlist(newHiddenFromHotlist);
    };

    const didHideEverywhereFail =
        hideEverywhereResult != null &&
        hideEverywhereResult.status !== "loading" &&
        (hideEverywhereResult.status !== "success" ||
            hideEverywhereResult.data.hideProgramEverywhere?.error != null);
    const didHideFromHotlistFail =
        hideFromHotlistResult != null &&
        hideFromHotlistResult.status !== "loading" &&
        (hideFromHotlistResult.status !== "success" ||
            hideFromHotlistResult.data.hideProgramFromHotlist?.error != null);

    return (
        <>
            <Button
                kind="secondary"
                disabled={
                    // disable this button while the other button's mutation is
                    // in progress, so that they can't both be in progress at
                    // the same time
                    hideFromHotlistResult?.status === "loading"
                }
                spinner={
                    // show a spinner and disable the button while this button's
                    // mutation is in progress
                    hideEverywhereResult?.status === "loading"
                }
                onClick={handleHideEverywhere}
                startIcon={didHideEverywhereFail ? cloudWarning : undefined}
            >
                {hiddenEverywhere
                    ? doNotTranslateYet`Undo hide everywhere`
                    : doNotTranslateYet`Hide everywhere`}
            </Button>
            <Button
                kind="secondary"
                disabled={
                    // we don't allow unhiding from the hotlist if the program
                    // is hidden everywhere
                    (hiddenEverywhere && hiddenFromHotlist) ||
                    // disable this button while the other button's mutation is
                    // in progress, so that they can't both be in progress at
                    // the same time
                    hideEverywhereResult?.status === "loading"
                }
                spinner={
                    // show a spinner and disable the button while this button's
                    // mutation is in progress
                    hideFromHotlistResult?.status === "loading"
                }
                onClick={handleHideFromHotlist}
                startIcon={didHideFromHotlistFail ? cloudWarning : undefined}
            >
                {hiddenFromHotlist
                    ? doNotTranslateYet`Undo hide from hotlist`
                    : doNotTranslateYet`Hide from hotlist`}
            </Button>
        </>
    );
}
